import React, { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import apiService, { AssetSettings, MonthlyActualBalance, FixedIncome, FixedExpense } from '../services/api';
import { usePortfolio } from '../hooks/usePortfolio';
import { useAuth } from '../hooks/useAuth';
import { localStorageUtils } from '../utils/localStorage';
import MonthlyAssetModal from '../components/MonthlyAssetModal';
import { DollarIcon, BarChartIcon, SettingsIcon } from '../components/Icons';
import LoadingOverlay from '../components/LoadingOverlay';
import { getApiFailureMessage } from '../utils/apiErrorMessage';
import '../components/AssetManagement.css';

// FixedIncome과 FixedExpense는 api.ts에서 import

interface MonthlySummary {
  year: number;
  month: number;
  totalIncome: number;
  totalExpense: number;
  balance: number;
  savings: number;
  accumulatedBalance: number; // 총 자산 (저축액, 퇴직연금 포함, 투자시드 제외)
  cashBalance: number; // 순수 현금 잔액 (저축액, 퇴직연금 제외, 투자시드 제외)
  actualBalance?: number; // 실제 금액
  difference?: number; // 차이
  investmentSeedAddition?: number; // 해당 월 투자시드 증액 (순수 현금에서 차감)
}

const AssetManagementPage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const [fixedIncomes, setFixedIncomes] = useState<FixedIncome[]>([]);
  const [fixedExpenses, setFixedExpenses] = useState<FixedExpense[]>([]);
  const [savings, setSavings] = useState<number>(0);
  const [existingSavings, setExistingSavings] = useState<number>(0);
  const [retirementPension, setRetirementPension] = useState<number>(0);
  const [investmentSeed, setInvestmentSeed] = useState<number>(0);
  
  // 폴딩 상태
  const [isFixedIncomeCollapsed, setIsFixedIncomeCollapsed] = useState(false);
  const [isFixedExpenseCollapsed, setIsFixedExpenseCollapsed] = useState(false);
  
  // 포트폴리오 정보 가져오기
  const { summary: portfolioSummary } = usePortfolio();
  const [monthlySummaries, setMonthlySummaries] = useState<MonthlySummary[]>([]);
  const [startYear, setStartYear] = useState<number>(new Date().getFullYear());
  const [startMonth, setStartMonth] = useState<number>(new Date().getMonth() + 1);
  const [endYear, setEndYear] = useState<number>(new Date().getFullYear());
  const [endMonth, setEndMonth] = useState<number>(new Date().getMonth() + 1);
  
  // 자산 설정
  const [assetSettings, setAssetSettings] = useState<AssetSettings | null>(null);
  const [startDate, setStartDate] = useState<string>(format(new Date(), 'yyyy-MM-dd'));
  const [initialBalance, setInitialBalance] = useState<string>('0');
  
  // 총 수입/지출 계산 (단순 합산 - 진짜 고정 수입/지출만)
  const { totalIncome, totalExpense } = React.useMemo(() => {
    // 고정 수입/지출은 매월 동일한 금액이므로 단순 합산
    const totalIncome = fixedIncomes.reduce((sum, income) => sum + income.amount, 0);
    const totalExpense = fixedExpenses.reduce((sum, expense) => sum + expense.amount, 0);

    return {
      totalIncome,
      totalExpense
    };
  }, [fixedIncomes, fixedExpenses]);
  
  
  const [newIncomeName, setNewIncomeName] = useState('');
  const [newIncomeAmount, setNewIncomeAmount] = useState('');
  const [newIncomeDay, setNewIncomeDay] = useState('');
  const [newIncomeStartDate, setNewIncomeStartDate] = useState<string>('');
  const [newIncomeEndDate, setNewIncomeEndDate] = useState<string>('');
  const [newExpenseName, setNewExpenseName] = useState('');
  const [newExpenseAmount, setNewExpenseAmount] = useState('');
  const [newExpenseDay, setNewExpenseDay] = useState('');
  const [newExpenseStartDate, setNewExpenseStartDate] = useState<string>('');
  const [newExpenseEndDate, setNewExpenseEndDate] = useState<string>('');
  const [showIncomeForm, setShowIncomeForm] = useState(false);
  const [showExpenseForm, setShowExpenseForm] = useState(false);
  const [editingIncomeId, setEditingIncomeId] = useState<number | null>(null);
  const [editingExpenseId, setEditingExpenseId] = useState<number | null>(null);
  const [showMonthlyModal, setShowMonthlyModal] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(false);
  
  // 모달에서 사용할 기간 (모달이 열려 있을 때 확장된 범위)
  const [modalStartYear, setModalStartYear] = useState<number>(new Date().getFullYear());
  const [modalStartMonth, setModalStartMonth] = useState<number>(new Date().getMonth() + 1);
  const [modalEndYear, setModalEndYear] = useState<number>(new Date().getFullYear());
  const [modalEndMonth, setModalEndMonth] = useState<number>(new Date().getMonth() + 1);

  // 모달 기간 변경 핸들러
  const handleModalPeriodChange = useCallback((newStartYear: number, newStartMonth: number, newEndYear: number, newEndMonth: number) => {
    setModalStartYear(newStartYear);
    setModalStartMonth(newStartMonth);
    setModalEndYear(newEndYear);
    setModalEndMonth(newEndMonth);
  }, []);

  // 자산 설정 로드
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const loadAll = async () => {
      setIsPageLoading(true);
      try {
        await Promise.all([loadAssetSettings(), loadFixedIncomes(), loadFixedExpenses()]);
      } finally {
        setIsPageLoading(false);
      }
    };
    void loadAll();
  }, [startYear, endYear]);

  useEffect(() => {
    // 의존성이 변경될 때마다 월별 요약 재계산
    calculateMonthlySummaries();
  }, [fixedIncomes, fixedExpenses, savings, existingSavings, assetSettings, startYear, endYear, startMonth, endMonth, portfolioSummary, investmentSeed, modalStartYear, modalEndYear, modalStartMonth, modalEndMonth]);

  const loadAssetSettings = async () => {
    try {
      const response = await apiService.getAssetSettings();
      if (response.success && response.data) {
        setAssetSettings(response.data);
        setStartDate(response.data.startDate);
        setInitialBalance(response.data.initialBalance.toString());
        
        // 계산 시작 날짜에서 연도와 월 추출하여 모달 초기값 설정
        const calcStartDate = new Date(response.data.startDate);
        setModalStartYear(calcStartDate.getFullYear());
        setModalStartMonth(calcStartDate.getMonth() + 1);
        // startYear/startMonth도 계산 시작 날짜로 설정
        setStartYear(calcStartDate.getFullYear());
        setStartMonth(calcStartDate.getMonth() + 1);
        
        if (response.data.savings !== undefined && response.data.savings !== null) {
          setSavings(response.data.savings);
        }
        if (response.data.existingSavings !== undefined && response.data.existingSavings !== null) {
          setExistingSavings(response.data.existingSavings);
        }
        if (response.data.retirementPension !== undefined && response.data.retirementPension !== null) {
          setRetirementPension(response.data.retirementPension);
        }
        if (response.data.investmentSeed !== undefined && response.data.investmentSeed !== null) {
          setInvestmentSeed(response.data.investmentSeed);
        }
      }
    } catch (error) {
      console.error('자산 설정 로드 실패:', error);
    }
  };
  
  const loadFixedIncomes = async () => {
    try {
      if (isAuthenticated) {
        // 로그인한 경우: DB에서 조회
        const response = await apiService.getFixedIncomes();
        if (response.success && response.data) {
          setFixedIncomes(response.data);
        }
      } else {
        // 로그인하지 않은 경우: 로컬스토리지에서 조회
        const localIncomes = localStorageUtils.getFixedIncomes();
        setFixedIncomes(localIncomes);
      }
    } catch (error) {
      console.error('고정 수입 로드 실패:', error);
    }
  };
  
  const loadFixedExpenses = async () => {
    try {
      if (isAuthenticated) {
        // 로그인한 경우: DB에서 조회
        const response = await apiService.getFixedExpenses();
        if (response.success && response.data) {
          setFixedExpenses(response.data);
        }
      } else {
        // 로그인하지 않은 경우: 로컬스토리지에서 조회
        const localExpenses = localStorageUtils.getFixedExpenses();
        setFixedExpenses(localExpenses);
      }
    } catch (error) {
      console.error('고정 지출 로드 실패:', error);
    }
  };
  

  // summaries를 인자로 받으면 그것을 사용, 없으면 현재 상태 사용
  // summaries는 calculateMonthlySummaries에서 이미 새로운 시작금액으로 계산됨
  const loadMonthlyBalances = async (summaries?: MonthlySummary[]) => {
    try {
      const effectiveStartYear = Math.min(startYear, modalStartYear);
      const effectiveEndYear = Math.max(endYear, modalEndYear);
      const yearsToLoad: number[] = [];
      for (let year = effectiveStartYear; year <= effectiveEndYear; year++) {
        yearsToLoad.push(year);
      }
      
      const allBalances: MonthlyActualBalance[] = [];
      for (const year of yearsToLoad) {
        const response = await apiService.getMonthlyActualBalancesByYear(year);
        if (response.success && response.data) {
          allBalances.push(...response.data);
        }
      }
      
      // summaries가 전달되었으면 그것을 사용하여 병합
      if (summaries && summaries.length > 0) {
        // 실제 금액이 있는 월들을 날짜순으로 정렬
        const actualBalanceMonths = summaries
          .map(summary => {
            const balance = allBalances.find(b => b.year === summary.year && b.month === summary.month);
            if (balance?.actualBalance !== undefined) {
              return { 
                summary, 
                actualBalance: balance.actualBalance,
                balance
              };
            }
            return null;
          })
          .filter(item => item !== null)
          .sort((a, b) => {
            const dateA = new Date(a!.summary.year, a!.summary.month - 1, 1);
            const dateB = new Date(b!.summary.year, b!.summary.month - 1, 1);
            return dateA.getTime() - dateB.getTime();
          });
        
        // 실제 금액을 기준으로 순수 현금과 누적 잔액 재계산
        // 기본 계산된 누적잔액(summary.accumulatedBalance)에 차이를 누적해서 더함
        // 이렇게 하면 고정 수입/지출 변경이 기본 누적잔액에 반영되고, 차이는 실제 금액과의 오차를 보정
        let previousActualCashBalance: number | null = null;
        let totalDifferenceAdjustment = 0; // 차이 누적
        
        const updated: MonthlySummary[] = [];
        
        for (let i = 0; i < summaries.length; i++) {
          const summary = summaries[i];
          const balance = allBalances.find(b => b.year === summary.year && b.month === summary.month);
          
          // 이전 월의 실제 금액 찾기
          let prevActualMonth = null;
          for (let j = i - 1; j >= 0; j--) {
            const prevBalance = allBalances.find(b => 
              b.year === summaries[j].year && b.month === summaries[j].month
            );
            if (prevBalance?.actualBalance !== undefined) {
              prevActualMonth = {
                year: summaries[j].year,
                month: summaries[j].month,
                actualBalance: prevBalance.actualBalance
              };
              break;
            }
          }
          
          // 현재 월의 실제 금액이 있는 경우
          if (balance?.actualBalance !== undefined) {
            // 순수 현금 계산 (이전 월 실제 금액 기준)
            const cashBalanceBasedOnPrevActual = prevActualMonth 
              ? prevActualMonth.actualBalance + summary.balance
              : summary.cashBalance;
            
            // 차이 = 실제 금액 - 순수 현금
            const difference = balance.actualBalance - cashBalanceBasedOnPrevActual;
            
            // 차이 누적
            totalDifferenceAdjustment += difference;
            
            // 누적잔액 = 기본 계산된 누적잔액 + 차이 누적
            // 고정 수입/지출 변경이 summary.accumulatedBalance에 이미 반영되어 있음
            const newAccumulatedBalance = summary.accumulatedBalance + totalDifferenceAdjustment;
            
            // 다음 달을 위한 순수 현금 기준점 업데이트
            previousActualCashBalance = balance.actualBalance;
            
            updated.push({
              ...summary,
              actualBalance: balance.actualBalance,
              difference,
              cashBalance: prevActualMonth ? cashBalanceBasedOnPrevActual : summary.cashBalance,
              accumulatedBalance: newAccumulatedBalance,
              investmentSeedAddition: balance?.investmentSeedAddition
            });
          } else {
            // 현재 월에 실제 금액이 없는 경우: 전월 순수현금 + 당월 잔액으로 표시
            const cashBalanceBasedOnPrevActual: number = previousActualCashBalance !== null
              ? previousActualCashBalance + summary.balance
              : summary.cashBalance;
            
            // 누적잔액 = 기본 계산된 누적잔액 + 차이 누적
            const newAccumulatedBalance = summary.accumulatedBalance + totalDifferenceAdjustment;
            
            // 다음 달을 위해 기준점 갱신 (실제금액이 없어도 계산된 순수현금으로 이어감)
            previousActualCashBalance = cashBalanceBasedOnPrevActual;
            
            updated.push({
              ...summary,
              actualBalance: balance?.actualBalance,
              difference: balance?.difference,
              cashBalance: cashBalanceBasedOnPrevActual,
              accumulatedBalance: newAccumulatedBalance,
              investmentSeedAddition: balance?.investmentSeedAddition
            });
          }
        }
        setMonthlySummaries(updated);
        return;
      }
      
      // summaries가 전달되지 않은 경우: 현재 상태를 기반으로 실제 금액만 병합
      setMonthlySummaries(prev => {
        if (prev.length <= 1) {
          return prev;
        }
        
        // 실제 금액이 있는 월들을 날짜순으로 정렬
        const actualBalanceMonths = prev
          .map(summary => {
            const balance = allBalances.find(b => b.year === summary.year && b.month === summary.month);
            if (balance?.actualBalance !== undefined) {
              return { 
                summary, 
                actualBalance: balance.actualBalance
              };
            }
            return null;
          })
          .filter(item => item !== null)
          .sort((a, b) => {
            const dateA = new Date(a!.summary.year, a!.summary.month - 1, 1);
            const dateB = new Date(b!.summary.year, b!.summary.month - 1, 1);
            return dateA.getTime() - dateB.getTime();
          });
        
        const updated = prev.map(summary => {
          const balance = allBalances.find(b => b.year === summary.year && b.month === summary.month);
          const summaryDate = new Date(summary.year, summary.month - 1, 1);
          
          // 이전에 입력된 실제 금액들의 조정량 합산
          // DB에 저장된 difference를 사용하여 시작금액 변경과 무관하게 유지
          let totalAdjustment = 0;
          for (const actualMonth of actualBalanceMonths) {
            const actualDate = new Date(actualMonth!.summary.year, actualMonth!.summary.month - 1, 1);
            if (actualDate < summaryDate) {
              const prevBalance = allBalances.find(b => 
                b.year === actualMonth!.summary.year && b.month === actualMonth!.summary.month
              );
              if (prevBalance?.difference !== undefined) {
                totalAdjustment += prevBalance.difference;
              } else {
                const prevSummary = prev.find(s => s.year === actualMonth!.summary.year && s.month === actualMonth!.summary.month);
                if (prevSummary) {
                  const adjustment = actualMonth!.actualBalance - prevSummary.cashBalance;
                  totalAdjustment += adjustment;
                }
              }
            }
          }
          
          // 차이 계산
          let difference: number | undefined;
          if (balance?.actualBalance !== undefined) {
            // 화면에 표시할 차이: 현재 기준으로 계산
            difference = balance.actualBalance - summary.cashBalance;
            // 누적잔액 조정: DB에 저장된 difference 사용
            const adjustmentDifference = balance.difference !== undefined ? balance.difference : difference;
            totalAdjustment += adjustmentDifference;
            
            return {
              ...summary,
              actualBalance: balance.actualBalance,
              difference,
              cashBalance: summary.cashBalance,
              accumulatedBalance: summary.accumulatedBalance + totalAdjustment,
              investmentSeedAddition: balance?.investmentSeedAddition ?? summary.investmentSeedAddition
            };
          }
          // 이전 월의 실제 금액 조정만 반영
          else if (totalAdjustment !== 0) {
            return {
              ...summary,
              actualBalance: balance?.actualBalance,
              difference: balance?.difference,
              cashBalance: summary.cashBalance,
              accumulatedBalance: summary.accumulatedBalance + totalAdjustment,
              investmentSeedAddition: balance?.investmentSeedAddition ?? summary.investmentSeedAddition
            };
          }
          
          return {
            ...summary,
            actualBalance: balance?.actualBalance,
            difference: balance?.difference,
            investmentSeedAddition: balance?.investmentSeedAddition ?? summary.investmentSeedAddition
          };
        });
        return updated;
      });
    } catch (error) {
      console.error('월별 실제 금액 로드 실패:', error);
    }
  };

  const saveAssetSettings = async () => {
    try {
      // savings가 undefined나 null이면 0으로 설정
      const savingsValue = savings !== undefined && savings !== null ? savings : 0;
      const existingSavingsValue = existingSavings !== undefined && existingSavings !== null ? existingSavings : 0;
      const retirementPensionValue = retirementPension !== undefined && retirementPension !== null ? retirementPension : 0;
      const investmentSeedValue = investmentSeed !== undefined && investmentSeed !== null ? investmentSeed : 0;
      
      const settingsData = {
        startDate,
        initialBalance: Number(initialBalance),
        savings: savingsValue,
        existingSavings: existingSavingsValue,
        retirementPension: retirementPensionValue,
        investmentSeed: investmentSeedValue
      };
      
      if (isAuthenticated) {
        // 로그인한 경우: DB에 저장
        const response = await apiService.saveAssetSettings(startDate, Number(initialBalance), savingsValue, existingSavingsValue, retirementPensionValue, investmentSeedValue);
        if (response.success) {
          // 새로운 assetSettings를 설정
          setAssetSettings(response.data);
          
          // 새로운 시작금액으로 월별 요약 재계산
          calculateMonthlySummaries(Number(initialBalance));
          
          alert('자산 설정이 저장되었습니다.');
        }
      } else {
        // 로그인하지 않은 경우: 로컬스토리지에 저장
        localStorageUtils.setAssetSettings(settingsData);
        setAssetSettings(settingsData as any);
        
        // 새로운 시작금액으로 월별 요약 재계산
        calculateMonthlySummaries(Number(initialBalance));
        
        alert('자산 설정이 저장되었습니다. (로컬 저장)');
      }
    } catch (error: any) {
      console.error('자산 설정 저장 실패:', error);
      alert(`자산 설정 저장 실패: ${getApiFailureMessage(error)}`);
    }
  };


  const calculateMonthlySummaries = (newInitialBalance?: number) => {
    const summaries: MonthlySummary[] = [];
    // 시작 금액 + 투자시드(포트폴리오 총시드) + 기존 저축액 + 퇴직연금
    // newInitialBalance가 전달되면 그것을 사용, 아니면 assetSettings에서 가져옴
    const initialBalanceValue = newInitialBalance !== undefined ? newInitialBalance : (assetSettings ? Number(assetSettings.initialBalance) : 0);
    const investmentSeedValue = investmentSeed || 0;
    const existingSavingsValue = existingSavings || 0;
    const retirementPensionValue = retirementPension || 0;
    let accumulatedBalance = initialBalanceValue + existingSavingsValue + retirementPensionValue;
    // 순수 현금 잔액 (저축액, 퇴직연금 제외, 투자시드 제외 - 투자시드는 시작금액에 포함되어 있으므로 제외)
    // 시작금액에서 투자시드를 빼서 순수 현금 계산
    let cashBalance = initialBalanceValue - investmentSeedValue;
    

    // 계산 시작 날짜를 날짜만 비교하도록 설정 (시간 제거)
    let calculationStartDate: Date | null = null;
    let calculationStartYear: number | null = null;
    let calculationStartMonth: number | null = null;
    let calculationStartDay: number | null = null;
    
    if (assetSettings?.startDate) {
      const startDateStr = assetSettings.startDate;
      // yyyy-MM-dd 형식의 문자열을 파싱하여 날짜만 사용
      const [year, month, day] = startDateStr.split('-').map(Number);
      calculationStartDate = new Date(year, month - 1, day);
      calculationStartYear = year;
      calculationStartMonth = month;
      calculationStartDay = day;
    }
    

    // 기간 내의 모든 월 계산 (모달 기간과 메인 기간 중 더 넓은 범위 사용)
    const effectiveStartDate = new Date(Math.min(startYear, modalStartYear), 
      startYear < modalStartYear ? startMonth - 1 : 
      (startYear === modalStartYear ? Math.min(startMonth, modalStartMonth) - 1 : modalStartMonth - 1), 1);
    // 종료월까지 포함하도록 계산
    const effectiveEndYear = Math.max(endYear, modalEndYear);
    const effectiveEndMonth = endYear > modalEndYear ? endMonth : 
      (endYear === modalEndYear ? Math.max(endMonth, modalEndMonth) : modalEndMonth);
    
    const effectiveStartYear = effectiveStartDate.getFullYear();
    const effectiveStartMonth = effectiveStartDate.getMonth() + 1;
    
    let currentYear = effectiveStartYear;
    let currentMonth = effectiveStartMonth;

    while (true) {
      // 종료년월을 초과하면 중단 (종료월까지 포함)
      if (currentYear > effectiveEndYear || 
          (currentYear === effectiveEndYear && currentMonth > effectiveEndMonth)) {
        break;
      }
      const currentDate = new Date(currentYear, currentMonth - 1, 1);

      // 계산 시작 날짜 이전이면 0으로 설정 (날짜만 비교)
      const isBeforeStartDate = calculationStartDate && (
        currentYear < calculationStartYear! || 
        (currentYear === calculationStartYear! && currentMonth < calculationStartMonth!)
      );
      
      if (isBeforeStartDate) {
        summaries.push({
          year: currentYear,
          month: currentMonth,
          totalIncome: 0,
          totalExpense: 0,
          balance: 0,
          savings: 0,
          accumulatedBalance: summaries.length > 0 ? summaries[summaries.length - 1].accumulatedBalance : accumulatedBalance,
          cashBalance: summaries.length > 0 ? summaries[summaries.length - 1].cashBalance : cashBalance
        });
      } else {
        // 고정 수입/지출 (startDate와 endDate를 고려하여 해당 월에 유효한 항목만 적용)
        let fixedIncomeTotal = 0;
        let fixedExpenseTotal = 0;
        
        // 현재 월의 첫날과 마지막날
        const currentMonthStart = new Date(currentYear, currentMonth - 1, 1);
        const currentMonthEnd = new Date(currentYear, currentMonth, 0); // 해당 월의 마지막 날
        // 시간을 00:00:00으로 설정하여 날짜만 비교
        currentMonthStart.setHours(0, 0, 0, 0);
        currentMonthEnd.setHours(23, 59, 59, 999);
        
        // 계산 시작일 이후의 월에만 고정 수입/지출 적용
        if (!calculationStartDate || 
            currentYear > calculationStartYear! || 
            (currentYear === calculationStartYear! && currentMonth >= calculationStartMonth!)) {
          
          // 같은 연도/월인 경우, 고정 수입/지출의 일자가 계산 시작일 이후면 적용
          if (currentYear === calculationStartYear! && currentMonth === calculationStartMonth! && calculationStartDay !== null) {
            fixedIncomeTotal = fixedIncomes
              .filter(income => {
                // 일자 체크
                const incomeDay = Number(income.day);
                if (incomeDay < calculationStartDay!) {
                  return false;
                }
                
                // startDate와 endDate 체크
                if (income.startDate) {
                  const startDate = new Date(income.startDate);
                  startDate.setHours(0, 0, 0, 0);
                  // 현재 월의 마지막 날이 시작일보다 이전이면 제외
                  if (currentMonthEnd < startDate) {
                    return false; // 시작일 이전
                  }
                }
                if (income.endDate) {
                  const endDate = new Date(income.endDate);
                  endDate.setHours(23, 59, 59, 999);
                  // 현재 월의 첫날이 종료일보다 이후이면 제외
                  if (currentMonthStart > endDate) {
                    return false; // 종료일 이후
                  }
                }
                
                return true;
              })
              .reduce((sum, income) => sum + income.amount, 0);
            
            fixedExpenseTotal = fixedExpenses
              .filter(expense => {
                // 일자 체크
                const expenseDay = Number(expense.day);
                if (expenseDay < calculationStartDay!) {
                  return false;
                }
                
                // startDate와 endDate 체크
                if (expense.startDate) {
                  const startDate = new Date(expense.startDate);
                  startDate.setHours(0, 0, 0, 0);
                  // 현재 월의 마지막 날이 시작일보다 이전이면 제외
                  if (currentMonthEnd < startDate) {
                    return false; // 시작일 이전
                  }
                }
                if (expense.endDate) {
                  const endDate = new Date(expense.endDate);
                  endDate.setHours(23, 59, 59, 999);
                  // 현재 월의 첫날이 종료일보다 이후이면 제외
                  if (currentMonthStart > endDate) {
                    return false; // 종료일 이후
                  }
                }
                
                return true;
              })
              .reduce((sum, expense) => sum + expense.amount, 0);
          } else {
            // 이후 연도/월이면 해당 월에 유효한 고정 수입/지출만 적용
            fixedIncomeTotal = fixedIncomes
              .filter(income => {
                // startDate와 endDate 체크
                if (income.startDate) {
                  const startDate = new Date(income.startDate);
                  startDate.setHours(0, 0, 0, 0);
                  // 현재 월의 마지막 날이 시작일보다 이전이면 제외
                  if (currentMonthEnd < startDate) {
                    return false; // 시작일 이전
                  }
                }
                if (income.endDate) {
                  const endDate = new Date(income.endDate);
                  endDate.setHours(23, 59, 59, 999);
                  // 현재 월의 첫날이 종료일보다 이후이면 제외
                  if (currentMonthStart > endDate) {
                    return false; // 종료일 이후
                  }
                }
                
                return true;
              })
              .reduce((sum, income) => sum + income.amount, 0);
            
            fixedExpenseTotal = fixedExpenses
              .filter(expense => {
                // startDate와 endDate 체크
                if (expense.startDate) {
                  const startDate = new Date(expense.startDate);
                  startDate.setHours(0, 0, 0, 0);
                  // 현재 월의 마지막 날이 시작일보다 이전이면 제외
                  if (currentMonthEnd < startDate) {
                    return false; // 시작일 이전
                  }
                }
                if (expense.endDate) {
                  const endDate = new Date(expense.endDate);
                  endDate.setHours(23, 59, 59, 999);
                  // 현재 월의 첫날이 종료일보다 이후이면 제외
                  if (currentMonthStart > endDate) {
                    return false; // 종료일 이후
                  }
                }
                
                return true;
              })
              .reduce((sum, expense) => sum + expense.amount, 0);
          }
        }
        
        const totalIncome = fixedIncomeTotal;
        const totalExpense = fixedExpenseTotal;
        const balance = totalIncome - totalExpense - savings;
        // 누적 잔액에는 저축액도 포함되어야 함 (balance + savings)
        accumulatedBalance += balance + savings;
        // 순수 현금 잔액 = 시작 금액 + (수입 - 지출 - 저축액)의 누적
        cashBalance += balance;

        summaries.push({
          year: currentYear,
          month: currentMonth,
          totalIncome,
          totalExpense,
          balance,
          savings,
          accumulatedBalance,
          cashBalance
        });
      }

      // 다음 월로 이동
      currentMonth++;
      if (currentMonth > 12) {
        currentMonth = 1;
        currentYear++;
      }
    }

    // 먼저 새로운 시작금액으로 계산된 summaries를 설정
    // 이렇게 하면 화면에 바로 반영됨
    setMonthlySummaries(summaries);
    
    // 그 다음 실제 금액을 로드하여 조정량 반영
    loadMonthlyBalances(summaries);
  };

  const addFixedIncome = async () => {
    if (!newIncomeName || !newIncomeAmount || !newIncomeDay) {
      alert('모든 필드를 입력해주세요.');
      return;
    }

    try {
      const incomeData = {
        name: newIncomeName,
        amount: Number(newIncomeAmount),
        day: Number(newIncomeDay),
        startDate: newIncomeStartDate || undefined,
        endDate: newIncomeEndDate || undefined
      };
      
      if (isAuthenticated) {
        // 로그인한 경우: DB에 저장
        let response: { success: boolean; data?: FixedIncome; message?: string };
        if (editingIncomeId) {
          response = await apiService.updateFixedIncome(
            editingIncomeId,
            incomeData.name,
            incomeData.amount,
            incomeData.day,
            newIncomeStartDate || undefined,
            newIncomeEndDate || undefined
          );
        } else {
          response = await apiService.createFixedIncome(
            incomeData.name,
            incomeData.amount,
            incomeData.day,
            newIncomeStartDate || undefined,
            newIncomeEndDate || undefined
          );
        }
        
        if (response.success && response.data) {
          const updatedIncome = response.data;
          if (editingIncomeId) {
            setFixedIncomes(fixedIncomes.map(income => 
              income.id === editingIncomeId ? updatedIncome : income
            ));
            alert('고정 수입이 수정되었습니다.');
          } else {
            setFixedIncomes([...fixedIncomes, updatedIncome]);
            alert('고정 수입이 추가되었습니다.');
          }
        }
      } else {
        // 로그인하지 않은 경우: 로컬스토리지에 저장
        const localIncomes = localStorageUtils.getFixedIncomes();
        if (editingIncomeId) {
          const index = localIncomes.findIndex(inc => inc.id === editingIncomeId);
          if (index !== -1) {
            localIncomes[index] = { ...localIncomes[index], ...incomeData };
            localStorageUtils.setFixedIncomes(localIncomes);
            setFixedIncomes([...localIncomes]);
            alert('고정 수입이 수정되었습니다. (로컬 저장)');
          }
        } else {
          const newIncome = {
            ...incomeData,
            id: Date.now(),
            createdAt: new Date().toISOString()
          };
          localIncomes.push(newIncome);
          localStorageUtils.setFixedIncomes(localIncomes);
          setFixedIncomes([...localIncomes]);
          alert('고정 수입이 추가되었습니다. (로컬 저장)');
        }
      }
      
      setNewIncomeName('');
      setNewIncomeAmount('');
      setNewIncomeDay('');
      setNewIncomeStartDate('');
      setNewIncomeEndDate('');
      setShowIncomeForm(false);
      setEditingIncomeId(null);
    } catch (error: any) {
      console.error('고정 수입 추가/수정 실패:', error);
      alert(`고정 수입 추가/수정 실패: ${getApiFailureMessage(error)}`);
    }
  };

  const addFixedExpense = async () => {
    if (!newExpenseName || !newExpenseAmount || !newExpenseDay) {
      alert('모든 필드를 입력해주세요.');
      return;
    }

    try {
      const expenseData = {
        name: newExpenseName,
        amount: Number(newExpenseAmount),
        day: Number(newExpenseDay),
        startDate: newExpenseStartDate || undefined,
        endDate: newExpenseEndDate || undefined
      };
      
      if (isAuthenticated) {
        // 로그인한 경우: DB에 저장
        let response: { success: boolean; data?: FixedExpense; message?: string };
        if (editingExpenseId) {
          response = await apiService.updateFixedExpense(
            editingExpenseId,
            expenseData.name,
            expenseData.amount,
            expenseData.day,
            newExpenseStartDate || undefined,
            newExpenseEndDate || undefined
          );
        } else {
          response = await apiService.createFixedExpense(
            expenseData.name,
            expenseData.amount,
            expenseData.day,
            newExpenseStartDate || undefined,
            newExpenseEndDate || undefined
          );
        }
        
        if (response.success && response.data) {
          const updatedExpense = response.data;
          if (editingExpenseId) {
            setFixedExpenses(fixedExpenses.map(expense => 
              expense.id === editingExpenseId ? updatedExpense : expense
            ));
            alert('고정 지출이 수정되었습니다.');
          } else {
            setFixedExpenses([...fixedExpenses, updatedExpense]);
            alert('고정 지출이 추가되었습니다.');
          }
        }
      } else {
        // 로그인하지 않은 경우: 로컬스토리지에 저장
        const localExpenses = localStorageUtils.getFixedExpenses();
        if (editingExpenseId) {
          const index = localExpenses.findIndex(exp => exp.id === editingExpenseId);
          if (index !== -1) {
            localExpenses[index] = { ...localExpenses[index], ...expenseData };
            localStorageUtils.setFixedExpenses(localExpenses);
            setFixedExpenses([...localExpenses]);
            alert('고정 지출이 수정되었습니다. (로컬 저장)');
          }
        } else {
          const newExpense = {
            ...expenseData,
            id: Date.now(),
            createdAt: new Date().toISOString()
          };
          localExpenses.push(newExpense);
          localStorageUtils.setFixedExpenses(localExpenses);
          setFixedExpenses([...localExpenses]);
          alert('고정 지출이 추가되었습니다. (로컬 저장)');
        }
      }
      
      setNewExpenseName('');
      setNewExpenseAmount('');
      setNewExpenseDay('');
      setNewExpenseStartDate('');
      setNewExpenseEndDate('');
      setShowExpenseForm(false);
      setEditingExpenseId(null);
    } catch (error: any) {
      console.error('고정 지출 추가/수정 실패:', error);
      alert(`고정 지출 추가/수정 실패: ${getApiFailureMessage(error)}`);
    }
  };

  const deleteFixedIncome = async (id: number) => {
    if (!window.confirm('이 고정 수입을 삭제하시겠습니까?')) {
      return;
    }
    
    try {
      const response = await apiService.deleteFixedIncome(id);
      if (response.success) {
        setFixedIncomes(fixedIncomes.filter(income => income.id !== id));
        alert('고정 수입이 삭제되었습니다.');
      }
    } catch (error: any) {
      console.error('고정 수입 삭제 실패:', error);
      alert(`고정 수입 삭제 실패: ${getApiFailureMessage(error)}`);
    }
  };

  const deleteFixedExpense = async (id: number) => {
    if (!window.confirm('이 고정 지출을 삭제하시겠습니까?')) {
      return;
    }
    
    try {
      const response = await apiService.deleteFixedExpense(id);
      if (response.success) {
        setFixedExpenses(fixedExpenses.filter(expense => expense.id !== id));
        alert('고정 지출이 삭제되었습니다.');
      }
    } catch (error: any) {
      console.error('고정 지출 삭제 실패:', error);
      alert(`고정 지출 삭제 실패: ${getApiFailureMessage(error)}`);
    }
  };
  


  return (
    <div className="asset-management">
      <LoadingOverlay isVisible={isPageLoading} message="자산 관리 데이터를 불러오는 중..." />
      <div className="asset-header">
        <h2><span style={{ display: 'inline-flex', alignItems: 'center', marginRight: '8px' }}><DollarIcon size={24} color="currentColor" /></span> 자산관리</h2>
        <p>고정 수입과 지출을 관리하고 월별 자산 변화를 추적하세요</p>
      </div>

      {/* 상단: 요약 카드 */}
      <div className="summary-section">
        <div className="summary-header-row">
          <h3>월별 자산 요약</h3>
          <button 
            className="open-modal-btn"
            onClick={() => {
              const calcStartDate = new Date(startDate);
              const calcStartYear = calcStartDate.getFullYear();
              const calcStartMonth = calcStartDate.getMonth() + 1;
              setModalStartYear(calcStartYear);
              setModalStartMonth(calcStartMonth);
              setModalEndYear(endYear);
              setModalEndMonth(endMonth);
              setShowMonthlyModal(true);
            }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', marginRight: '4px' }}><BarChartIcon size={16} color="#fff" /></span> 상세 보기
          </button>
        </div>
        <div className="summary-cards-grid">
          <div className="summary-card-item">
            <div className="card-label">총 수입</div>
            <div className="card-amount income">{totalIncome.toLocaleString()}원</div>
          </div>
          <div className="summary-card-item">
            <div className="card-label">총 지출</div>
            <div className="card-amount expense">{totalExpense.toLocaleString()}원</div>
          </div>
          <div className="summary-card-item">
            <div className="card-label">순수익</div>
            <div className={`card-amount ${totalIncome - totalExpense >= 0 ? 'positive' : 'negative'}`}>
              {(totalIncome - totalExpense).toLocaleString()}원
            </div>
          </div>
        </div>
      </div>

      {/* 메인 컨텐츠: 2열 그리드 */}
      <div className="asset-content-grid">
        {/* 왼쪽: 고정 수입/지출 */}
        <div className="fixed-transactions">
          <div className="settings-section">
            <div className="section-header">
              <div className="section-title-group">
                <button 
                  className="collapse-btn"
                  onClick={() => setIsFixedIncomeCollapsed(!isFixedIncomeCollapsed)}
                >
                  {isFixedIncomeCollapsed ? '▶' : '▼'}
                </button>
                <h3>고정 수입</h3>
              </div>
              <button 
                className="add-btn"
                onClick={() => setShowIncomeForm(!showIncomeForm)}
              >
                + 추가
              </button>
            </div>
            
            {!isFixedIncomeCollapsed && (
              <>
            
            {showIncomeForm && (
              <div className="form-section">
                <div className="form-group">
                  <label>수입 항목명 *</label>
                  <input
                    type="text"
                    placeholder="수입 항목명을 입력하세요"
                    value={newIncomeName}
                    onChange={(e) => setNewIncomeName(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>금액 *</label>
                  <input
                    type="number"
                    placeholder="금액을 입력하세요"
                    value={newIncomeAmount}
                    onChange={(e) => setNewIncomeAmount(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>일자 (1-31) *</label>
                  <input
                    type="number"
                    placeholder="매월 며칠에 받는지 입력하세요"
                    min="1"
                    max="31"
                    value={newIncomeDay}
                    onChange={(e) => setNewIncomeDay(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>시작 날짜 (선택)</label>
                  <input
                    type="date"
                    placeholder="시작 날짜 (yyyy-MM-dd)"
                    value={newIncomeStartDate}
                    onChange={(e) => setNewIncomeStartDate(e.target.value)}
                  />
                  <small>비워두면 계산 시작 날짜부터 적용됩니다</small>
                </div>
                <div className="form-group">
                  <label>종료 날짜 (선택)</label>
                  <input
                    type="date"
                    placeholder="종료 날짜 (yyyy-MM-dd)"
                    value={newIncomeEndDate}
                    onChange={(e) => setNewIncomeEndDate(e.target.value)}
                  />
                  <small>비워두면 무제한 적용됩니다</small>
                </div>
                <div className="form-actions">
                  <button onClick={addFixedIncome}>{editingIncomeId ? '수정' : '추가'}</button>
                    <button onClick={() => {
                      setShowIncomeForm(false);
                      setEditingIncomeId(null);
                      setNewIncomeName('');
                      setNewIncomeAmount('');
                      setNewIncomeDay('');
                      setNewIncomeStartDate('');
                      setNewIncomeEndDate('');
                    }}>취소</button>
                </div>
              </div>
            )}

            <div className="items-list">
              {fixedIncomes.length === 0 ? (
                <p className="empty-message">고정 수입이 없습니다.</p>
              ) : (
                fixedIncomes.map(income => (
                  <div key={income.id} className="item-row">
                    <span className="item-name">{income.name}</span>
                    <span className="item-amount">{income.amount.toLocaleString()}원</span>
                    <span className="item-day">매월 {income.day}일</span>
                    <button 
                      className="edit-btn"
                      onClick={() => {
                        setEditingIncomeId(income.id);
                        setNewIncomeName(income.name);
                        setNewIncomeAmount(income.amount.toString());
                        setNewIncomeDay(income.day.toString());
                        setNewIncomeStartDate(income.startDate || '');
                        setNewIncomeEndDate(income.endDate || '');
                        setShowIncomeForm(true);
                      }}
                    >
                      수정
                    </button>
                    <button 
                      className="delete-btn"
                      onClick={() => deleteFixedIncome(income.id)}
                    >
                      삭제
                    </button>
                  </div>
                ))
              )}
            </div>
              </>
            )}
          </div>

          <div className="settings-section">
            <div className="section-header">
              <div className="section-title-group">
                <button 
                  className="collapse-btn"
                  onClick={() => setIsFixedExpenseCollapsed(!isFixedExpenseCollapsed)}
                >
                  {isFixedExpenseCollapsed ? '▶' : '▼'}
                </button>
                <h3>고정 지출</h3>
              </div>
              <button 
                className="add-btn"
                onClick={() => setShowExpenseForm(!showExpenseForm)}
              >
                + 추가
              </button>
            </div>
            
            {!isFixedExpenseCollapsed && (
              <>
            
            {showExpenseForm && (
              <div className="form-section">
                <div className="form-group">
                  <label>지출 항목명 *</label>
                  <input
                    type="text"
                    placeholder="지출 항목명을 입력하세요"
                    value={newExpenseName}
                    onChange={(e) => setNewExpenseName(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>금액 *</label>
                  <input
                    type="number"
                    placeholder="금액을 입력하세요"
                    value={newExpenseAmount}
                    onChange={(e) => setNewExpenseAmount(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>일자 (1-31) *</label>
                  <input
                    type="number"
                    placeholder="매월 며칠에 지출하는지 입력하세요"
                    min="1"
                    max="31"
                    value={newExpenseDay}
                    onChange={(e) => setNewExpenseDay(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>시작 날짜 (선택)</label>
                  <input
                    type="date"
                    placeholder="시작 날짜 (yyyy-MM-dd)"
                    value={newExpenseStartDate}
                    onChange={(e) => setNewExpenseStartDate(e.target.value)}
                  />
                  <small>비워두면 계산 시작 날짜부터 적용됩니다</small>
                </div>
                <div className="form-group">
                  <label>종료 날짜 (선택)</label>
                  <input
                    type="date"
                    placeholder="종료 날짜 (yyyy-MM-dd)"
                    value={newExpenseEndDate}
                    onChange={(e) => setNewExpenseEndDate(e.target.value)}
                  />
                  <small>비워두면 무제한 적용됩니다</small>
                </div>
                <div className="form-actions">
                  <button onClick={addFixedExpense}>{editingExpenseId ? '수정' : '추가'}</button>
                    <button onClick={() => {
                      setShowExpenseForm(false);
                      setEditingExpenseId(null);
                      setNewExpenseName('');
                      setNewExpenseAmount('');
                      setNewExpenseDay('');
                      setNewExpenseStartDate('');
                      setNewExpenseEndDate('');
                    }}>취소</button>
                </div>
              </div>
            )}

            <div className="items-list">
              {fixedExpenses.length === 0 ? (
                <p className="empty-message">고정 지출이 없습니다.</p>
              ) : (
                fixedExpenses.map(expense => (
                  <div key={expense.id} className="item-row">
                    <span className="item-name">{expense.name}</span>
                    <span className="item-amount">{expense.amount.toLocaleString()}원</span>
                    <span className="item-day">매월 {expense.day}일</span>
                    <button 
                      className="edit-btn"
                      onClick={() => {
                        setEditingExpenseId(expense.id);
                        setNewExpenseName(expense.name);
                        setNewExpenseAmount(expense.amount.toString());
                        setNewExpenseDay(expense.day.toString());
                        setNewExpenseStartDate(expense.startDate || '');
                        setNewExpenseEndDate(expense.endDate || '');
                        setShowExpenseForm(true);
                      }}
                    >
                      수정
                    </button>
                    <button 
                      className="delete-btn"
                      onClick={() => deleteFixedExpense(expense.id)}
                    >
                      삭제
                    </button>
                  </div>
                ))
              )}
            </div>
              </>
            )}
          </div>
        </div>

        {/* 오른쪽: 자산 설정 */}
        <div className="asset-settings-panel">
          {/* 자산 항목 그리드 */}
          <div className="settings-section">
            <h3>자산 항목</h3>
            <div className="asset-items-grid">
              <div className="asset-item">
                <label>월 저축액</label>
                <div className="input-with-unit">
                  <input
                    type="number"
                    placeholder="0"
                    value={savings !== undefined && savings !== null ? savings : ''}
                    onChange={(e) => setSavings(Number(e.target.value) || 0)}
                  />
                  <span>원</span>
                </div>
              </div>
              <div className="asset-item">
                <label>기존 저축액</label>
                <div className="input-with-unit">
                  <input
                    type="number"
                    placeholder="0"
                    value={existingSavings !== undefined && existingSavings !== null ? existingSavings : ''}
                    onChange={(e) => setExistingSavings(Number(e.target.value) || 0)}
                  />
                  <span>원</span>
                </div>
              </div>
              <div className="asset-item">
                <label>퇴직연금</label>
                <div className="input-with-unit">
                  <input
                    type="number"
                    placeholder="0"
                    value={retirementPension !== undefined && retirementPension !== null ? retirementPension : ''}
                    onChange={(e) => setRetirementPension(Number(e.target.value) || 0)}
                  />
                  <span>원</span>
                </div>
              </div>
              <div className="asset-item">
                <label>초기 투자시드</label>
                <div className="input-with-unit">
                  <input
                    type="number"
                    placeholder="0"
                    value={investmentSeed !== undefined && investmentSeed !== null ? investmentSeed : ''}
                    onChange={(e) => setInvestmentSeed(Number(e.target.value) || 0)}
                  />
                  <span>원</span>
                </div>
                <small className="input-hint">초기 투자시드 + 월별 투자시드 증액(월별 자산 변화에서 입력)이 홈의 총 시드로 반영됩니다</small>
              </div>
            </div>
          </div>

          {/* 계산 설정 */}
          <div className="settings-section">
            <h3>계산 설정</h3>
            <div className="calculation-settings">
              <div className="calc-item">
                <label>계산 시작 날짜</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className="calc-item">
                <label>시작 금액</label>
                <div className="input-with-unit">
                  <input
                    type="number"
                    placeholder="0"
                    value={initialBalance}
                    onChange={(e) => setInitialBalance(e.target.value)}
                  />
                  <span>원</span>
                </div>
              </div>
            </div>
            <button onClick={saveAssetSettings} className="save-settings-btn">
              <span style={{ display: 'inline-flex', alignItems: 'center', marginRight: '4px' }}><SettingsIcon size={16} color="#fff" /></span> 설정 저장
            </button>
          </div>
        </div>
      </div>

      <MonthlyAssetModal
        isOpen={showMonthlyModal}
        onClose={() => {
          setShowMonthlyModal(false);
          // 모달이 닫힐 때 모달 기간을 리셋하지 않음 (사용자가 설정한 기간 유지)
        }}
        monthlySummaries={monthlySummaries}
        onSummariesChange={setMonthlySummaries}
        initialStartYear={modalStartYear}
        initialStartMonth={modalStartMonth}
        initialEndYear={modalEndYear}
        initialEndMonth={modalEndMonth}
        onPeriodChange={handleModalPeriodChange}
      />
    </div>
  );
};

export default AssetManagementPage;

